import {ThemeProvider} from '@mui/material/styles';
import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom';

import Shell from './components/Shell';
import Header from './components/Header';
import LeftSidebar from './components/LeftSidebar';
import MainArea from './components/MainArea';
import {BookViewer} from './components/BookViewer';
import LoginScreen from './components/LoginScreen';
import UnauthorisedScreen from './components/UnauthorisedScreen';
import AdminDashboard from './components/AdminDashboard';
import {$post, clearCsrf} from './lib/requests';
import md3Theme from './lib/theme';

export interface AuthUser {
  email?: string;
  picture?: string;
  name?: string;
  role?: string;
}

export interface AuthState {
  isLoggedIn?: boolean;
  unauthorised?: boolean;
  isAdmin?: boolean;
  user?: AuthUser;
}

export interface AuthContextType {
  auth?: AuthState | null;
  setAuth?: (auth: AuthState | null) => void;
  checkAuth?: () => void;
}

export interface IQueryContext {
  searchString: string;
  setSearchString: (val: string) => void;
  fixedQuery: string | null;
  setFixedQuery: (val: string | null) => void;
  resetSearchState?: () => void;
}

const defaultQueryContext: IQueryContext = {
  searchString: '',
  setSearchString: () => {},
  fixedQuery: '',
  setFixedQuery: () => {},
  resetSearchState: () => {},
};

export const QueryContext = createContext<IQueryContext>(defaultQueryContext);
export const AuthContext = createContext<AuthContextType>({});

export function useAuthRedirects(): boolean {
  const {isLoggedIn, unauthorised} = useContext(AuthContext).auth || {};
  const navigate = useNavigate();
  const {pathname} = useLocation();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    } else if (unauthorised) {
      navigate('/401');
    } else if (pathname === '/login' || pathname === '/401') {
      navigate('/');
    }
  }, [isLoggedIn, navigate, pathname, unauthorised]);

  return Boolean(isLoggedIn && !unauthorised);
}

function ProtectedRoute({
  children,
  requireAdmin,
  requireVolunteer,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireVolunteer?: boolean;
}) {
  const authed = useAuthRedirects();
  const {auth} = useContext(AuthContext);

  if (!authed) return null;

  if (requireAdmin && !auth?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireVolunteer && auth?.user?.role !== 'VOLUNTEER') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const [searchString, setSearchString] = useState('');
  const [fixedQuery, setFixedQuery] = useState<string | null>('');
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [loading, setLoading] = useState(true);

  function resetSearchState() {
    setSearchString('');
    setFixedQuery('');
  }

  async function autoAuth() {
    try {
      const authState = await $post<AuthState>('/login', {});
      clearCsrf();
      setAuth(authState);
    } catch {
      setAuth(null);
    } finally {
      setLoading(false);
    }
  }

  useLayoutEffect(() => {
    autoAuth();
  }, []);

  if (loading) return <div />;

  const queryContextValue: IQueryContext = {
    searchString,
    setSearchString,
    fixedQuery,
    setFixedQuery,
    resetSearchState,
  };

  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <QueryContext.Provider value={queryContextValue}>
          <Shell
            header={<Header />}
            leftSidebar={<LeftSidebar />}
            rightSidebar={null}
            main={<MainArea />}
          />
        </QueryContext.Provider>
      ),
    },
    {
      path: '/book/:bookid',
      element: <BookViewer />,
    },
    {
      path: '/login',
      element: <LoginScreen />,
    },
    {
      path: '/401',
      element: <UnauthorisedScreen />,
    },
    {
      path: '/admin',
      element: (
        <ProtectedRoute requireAdmin>
          <QueryContext.Provider value={queryContextValue}>
            <Shell
              header={<Header />}
              leftSidebar={<LeftSidebar />}
              rightSidebar={null}
              main={<AdminDashboard />}
            />
          </QueryContext.Provider>
        </ProtectedRoute>
      ),
    },
    {
      path: '/volunteer',
      element: (
        <ProtectedRoute requireVolunteer>
          <QueryContext.Provider value={queryContextValue}>
            <Shell
              header={<Header />}
              leftSidebar={<LeftSidebar />}
              rightSidebar={null}
              main={<MainArea assignedTo={auth?.user?.email} />}
            />
          </QueryContext.Provider>
        </ProtectedRoute>
      ),
    },
  ]);

  return (
    <ThemeProvider theme={md3Theme}>
      <AuthContext.Provider value={{auth, setAuth}}>
        <RouterProvider router={router} />
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
